import { cva, type VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { cn } from '../cn.js';

/**
 * How a navigation link looks, and how it looks when it is where you are.
 *
 * A recipe rather than a component because the link is the router's: each application hands its
 * own `Link` this class, the way `buttonVariants` is handed to a menu trigger. The current item is
 * selected by **`aria-current="page"`**, which the router sets — so the state a screen reader
 * announces and the state a person sees are one attribute and cannot disagree.
 *
 * Before this the console's navigation had no current state at all and the workspace's was a
 * weight change on grey text (M5.5-01). Every place that navigates — the header, the
 * administration sidebar, Supervise's two views — now marks the current one the same way.
 */
export const navItemVariants = cva(
  [
    'inline-flex cursor-pointer items-center gap-(--space-sm) text-(--color-text-secondary)',
    'hover:text-(--color-text-primary)',
    'outline-none focus-visible:ring-2 focus-visible:ring-(--color-focus-ring)',
    'transition-colors duration-(--motion-duration-fast) motion-reduce:transition-none',
  ],
  {
    variants: {
      orientation: {
        horizontal: [
          'h-(--size-control-sm) rounded-(--radius-md) px-(--space-sm) whitespace-nowrap',
          'text-(length:--font-size-body) hover:bg-(--color-surface-sunken)',
          'aria-[current=page]:bg-(--color-accent-subtle) aria-[current=page]:font-medium aria-[current=page]:text-(--color-accent)',
        ],
        vertical: [
          'w-full rounded-(--radius-md) px-(--space-sm) py-(--space-xs)',
          'text-(length:--font-size-body) hover:bg-(--color-surface-sunken)',
          'aria-[current=page]:bg-(--color-accent-subtle) aria-[current=page]:font-medium aria-[current=page]:text-(--color-accent)',
        ],
        /*
         * Views of one page that each have an address — Supervise's queues and agents. Drawn as
         * the underline tabs are, because to the person using them they are tabs; they are links
         * only so that each view can be bookmarked and sent.
         */
        tab: [
          'relative px-(--space-md) py-(--space-sm) font-medium whitespace-nowrap',
          'text-(length:--font-size-body)',
          'after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-(--radius-full)',
          'aria-[current=page]:text-(--color-text-primary) aria-[current=page]:after:bg-(--color-accent)',
        ],
      },
    },
    defaultVariants: { orientation: 'horizontal' },
  },
);

/** The strip `tab` links sit in: the underline tab list's border, and nothing else. */
export const navTabListClassName =
  'flex min-w-0 items-center gap-(--space-xs) overflow-x-auto overflow-y-hidden border-b border-(--color-border)';

export type NavItemVariants = VariantProps<typeof navItemVariants>;

export interface NavSectionProps {
  /** The group's name, or nothing for a list that needs no heading. */
  readonly title?: string | undefined;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}

/**
 * A titled group of vertical navigation links.
 *
 * The heading is sentence case and quiet: it was small capitals with tracking, the treatment that
 * reads as a template label, and it was louder than the links it was naming.
 */
export function NavSection({ title, children, className }: NavSectionProps) {
  return (
    <div className={cn('flex flex-col gap-(--space-xs)', className)}>
      {title === undefined ? null : (
        <h2 className="px-(--space-sm) text-(length:--font-size-caption) font-medium text-(--color-text-muted)">
          {title}
        </h2>
      )}
      <ul className="flex flex-col gap-px">{children}</ul>
    </div>
  );
}
